import UserInformationForm, {
  Errors,
  initialErrors,
  State,
} from 'components/UserInformation/UserInformationForm';
import { GenderValues } from 'enums/Genders';
import { professionalSituationRequiresDetails } from 'enums/ProfessionalSituation';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';

const initialState: State = {
  gender: 'NOT_SPECIFIED' as GenderValues,
  firstName: '',
  lastName: '',
  email: '',
  organism: '',
  professionalSituation: '',
  priority: false,
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  region: '',
  country: '',
  dateOfBirth: '',
  isMetropolitan: false,
  presentation: '',
  comments: '',
};

type Props = {
  mentor: User | undefined;
};
const EditInformationForm: FC<Props> = ({ mentor }) => {
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();

  const [state, setState] = useState<State>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);

  // Updates state when user data is loaded
  const refreshState = useCallback(() => {
    mentor &&
      setState({
        gender: mentor.gender || 'NOT_SPECIFIED',
        firstName: mentor.firstName || '',
        lastName: mentor.lastName || '',
        email: mentor.email?.replace(' ', '') || '',
        organism: mentor.organism || '',
        professionalSituation: mentor.professionalSituation || '',
        priority: mentor.priority || false,
        phone: mentor.phone || '',
        address: mentor.address || '',
        postalCode: mentor.postalCode || '',
        city: mentor.city || '',
        region: mentor.region || '',
        country: mentor.country || '',
        isMetropolitan: !!mentor.isMetropolitan,
        dateOfBirth: mentor.dateOfBirth
          ? new Date(mentor.dateOfBirth).toISOString().slice(0, 10)
          : '',
        presentation: mentor.presentation || '',
        comments: mentor.comments || '',
      });
  }, [mentor, setState]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  const handleCancel = () => {
    refreshState();
    if (mentor) {
      navigate(`/admin/mentoring/mentors/${mentor.id}/`);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (mentor) {
      if (!state.professionalSituation) {
        window.scrollTo({ top: 0 });
        return setErrors({ ...initialErrors, professionalSituation: true });
      }
      if (
        professionalSituationRequiresDetails(state.professionalSituation) &&
        !state.organism.trim()
      ) {
        window.scrollTo({ top: 0 });
        return setErrors({ ...initialErrors, organism: true });
      }

      setLoading(true);
      mutate(
        `/user/${mentor.id}`,
        {
          ...mentor,
          ...state,
          organism: professionalSituationRequiresDetails(
            state.professionalSituation,
          )
            ? state.organism.trim()
            : null,
        },
        false,
      );

      const newDateOfBirth = new Date(state.dateOfBirth);

      privateAPI
        .patch(`/user/${mentor.id}`, {
          ...state,
          phone: state.phone || null,
          dateOfBirth:
            newDateOfBirth.toString() === 'Invalid Date'
              ? null
              : newDateOfBirth,
          organism: professionalSituationRequiresDetails(
            state.professionalSituation,
          )
            ? state.organism.trim()
            : null,
        })
        .then(() => {
          setLoading(false);
          mutate(`/user/${mentor.id}`);
          navigate(`/admin/mentoring/mentors/${mentor.id}/`);
        })
        .catch((err) => {
          setLoading(false);
          if (err?.response?.data?.fields?.includes('email')) {
            setErrors({
              ...initialErrors,
              email: true,
            });
          } else {
            setErrors({
              ...initialErrors,
              general: true,
            });
          }
          window.scrollTo({ top: 0 });
        });
    }
  };

  return (
    <section>
      <UserInformationForm
        {...{
          state,
          setState,
          errors,
          setErrors,
          loading,
        }}
        variant="mentor"
        adminView
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </section>
  );
};

export default EditInformationForm;
